import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, BookOpen } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { ServiceCTA } from "../components/ServiceCTA";

const fmtDate = (ts) =>
  ts ? new Date(ts * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

const Blog = () => {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    fetch("/api/posts.php?status=published")
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo
        title="Blog — End of Tenancy Cleaning Tips & Advice London"
        description="Expert advice on end of tenancy cleaning in London. Tips for tenants, landlords and letting agents to pass checkout inspections and get deposits back."
        ogTitle="Blog | Prime Tenancy Cleaning London"
        ogDescription="Cleaning tips, tenancy guides and London property advice from Prime Tenancy Cleaning."
        path="/blog"
        schema={[
          breadcrumbSchema([{ name: "Blog", path: "/blog" }]),
        ]}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-14 md:pt-40 md:pb-24 bg-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-navy-light opacity-90" />
        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-block bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Advice & Guides
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              The PTC Blog
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              Expert guides on end of tenancy cleaning, deposit protection and London property — written for tenants, landlords and letting agents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="text-center py-20 text-slate-400">Loading posts…</div>
          ) : error ? (
            <div className="text-center py-20 text-slate-400">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Could not load posts right now. Please try again later.</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-semibold text-slate-600 mb-1">No posts yet</p>
              <p className="text-sm">Check back soon — guides and tips coming shortly.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md hover:border-teal/30 transition-all"
                >
                  {post.featuredImage ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-navy to-navy-light flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-gold/60" />
                    </div>
                  )}
                  <div className="p-6">
                    {post.focusKeyword && (
                      <span className="text-xs font-semibold text-teal uppercase tracking-wide">{post.focusKeyword}</span>
                    )}
                    <h2 className="font-heading text-xl font-semibold text-navy mt-2 mb-3 leading-snug group-hover:text-teal transition-colors">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                        <Calendar className="w-3.5 h-3.5" />
                        {fmtDate(post.createdAt)}
                      </div>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal hover:text-navy transition-colors"
                      >
                        Read more <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <ServiceCTA
        title="Need a quote for your London clean?"
        description="Fixed price, 7-day re-clean guarantee, all 32 London boroughs covered."
        variant="navy"
      />
    </>
  );
};

export default Blog;
